import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AlertTriangle, Lock, RotateCcw, Trash2, Loader2, Star, CalendarPlus } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * ManageEnrollmentsModal — Super Admin view of a student's enrollments with
 * per-row Suspend / Unsuspend / Delete actions. Progress records are kept on
 * Delete (option (i) from planning).
 */
export default function ManageEnrollmentsModal({ studentId, studentName, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyCid, setBusyCid] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null); // { course_id, course_name }
  const [suspendReason, setSuspendReason] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);   // { course_id, course_name }
  const [extendTarget, setExtendTarget] = useState(null);   // { course_id, course_name }
  const [extendMonths, setExtendMonths] = useState(3);

  const load = async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/students/${studentId}/enrollments`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load enrollments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && studentId) {
      setData(null);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, studentId]);

  const confirmSuspend = async () => {
    if (!suspendTarget) return;
    const reason = suspendReason.trim();
    if (!reason) {
      toast.error("Please enter a reason");
      return;
    }
    setBusyCid(suspendTarget.course_id);
    try {
      await api.post(`/admin/students/${studentId}/enrollments/${suspendTarget.course_id}/suspend`, { reason });
      toast.success(`Suspended ${suspendTarget.course_name}`);
      setSuspendTarget(null);
      setSuspendReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to suspend");
    } finally {
      setBusyCid(null);
    }
  };

  const doUnsuspend = async (cid, name) => {
    setBusyCid(cid);
    try {
      await api.post(`/admin/students/${studentId}/enrollments/${cid}/unsuspend`);
      toast.success(`Restored ${name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore");
    } finally {
      setBusyCid(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyCid(deleteTarget.course_id);
    try {
      await api.delete(`/admin/students/${studentId}/enrollments/${deleteTarget.course_id}`);
      toast.success(`Removed ${deleteTarget.course_name}. Progress kept in DB.`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setBusyCid(null);
    }
  };

  const confirmExtend = async () => {
    if (!extendTarget) return;
    const months = Math.max(1, Math.min(60, Number(extendMonths || 0)));
    setBusyCid(extendTarget.course_id);
    try {
      const r = await api.post(
        `/admin/students/${studentId}/enrollments/${extendTarget.course_id}/extend`,
        { months },
      );
      toast.success(
        `Extended ${extendTarget.course_name} by ${months} month(s). New expiry: ${(r.data?.access_expires_at || "").slice(0, 10)}`,
      );
      setExtendTarget(null);
      setExtendMonths(3);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to extend");
    } finally {
      setBusyCid(null);
    }
  };

  const rows = data?.enrollments || [];

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="max-w-2xl" data-testid="manage-enrollments-modal">
          <DialogHeader>
            <DialogTitle>Manage Enrollments</DialogTitle>
            <DialogDescription>
              {studentName ? `Student: ${studentName}` : ""}
              {" — Suspend restricts access with a reason shown to the student. Delete removes the enrollment but keeps their progress records (they can be re-enrolled later)."}
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          )}

          {!loading && rows.length === 0 && (
            <div className="py-8 text-center text-slate-500 text-sm">
              This student has no active enrollments.
            </div>
          )}

          <div className="space-y-3">
            {rows.map((e) => (
              <div
                key={e.course_id}
                className={`rounded-md border p-4 flex items-start gap-3 ${e.suspended ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}
                data-testid={`enrollment-row-${e.course_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800">{e.course_name}</span>
                    {e.is_primary && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-300 text-blue-700 flex items-center gap-1">
                        <Star className="w-3 h-3" /> Primary
                      </Badge>
                    )}
                    {e.course_type === "abstract_exam" && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-300 text-purple-700">
                        Abstract Exam
                      </Badge>
                    )}
                    {e.suspended && (
                      <Badge className="text-[10px] px-1.5 py-0 bg-amber-500 text-white flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Suspended
                      </Badge>
                    )}
                  </div>
                  {e.suspended && (
                    <div className="mt-2 text-xs text-amber-900 bg-amber-100 border border-amber-200 rounded px-2 py-1">
                      <span className="font-semibold">Reason:</span> {e.suspended_reason || "—"}
                      {e.suspended_by_name && (
                        <div className="mt-0.5 text-[10px] text-amber-700">by {e.suspended_by_name}</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setExtendTarget(e); setExtendMonths(3); }}
                    disabled={busyCid === e.course_id}
                    className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                    data-testid={`extend-${e.course_id}`}
                    title="Extend subscription"
                  >
                    <CalendarPlus className="w-4 h-4 mr-1" />
                    Extend
                  </Button>
                  {e.suspended ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => doUnsuspend(e.course_id, e.course_name)}
                      disabled={busyCid === e.course_id}
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      data-testid={`unsuspend-${e.course_id}`}
                    >
                      {busyCid === e.course_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                      Restore
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setSuspendTarget(e); setSuspendReason(""); }}
                      disabled={busyCid === e.course_id}
                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                      data-testid={`suspend-${e.course_id}`}
                    >
                      <Lock className="w-4 h-4 mr-1" />
                      Suspend
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteTarget(e)}
                    disabled={busyCid === e.course_id}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid={`delete-enrollment-${e.course_id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Suspend reason dialog */}
      <Dialog open={!!suspendTarget} onOpenChange={(o) => !o && setSuspendTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="suspend-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Suspend enrollment
            </DialogTitle>
            <DialogDescription>
              Provide a reason. The student will see this exact message when they try to access <span className="font-semibold">{suspendTarget?.course_name}</span>.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="e.g. Payment overdue. Please contact billing to restore access."
            rows={4}
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            data-testid="suspend-reason-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSuspendTarget(null)}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={confirmSuspend}
              disabled={!suspendReason.trim() || busyCid === suspendTarget?.course_id}
              data-testid="confirm-suspend"
            >
              {busyCid === suspendTarget?.course_id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Suspend Access"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-enrollment-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this enrollment?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes <span className="font-semibold">{deleteTarget?.course_name}</span> from the student. Their progress records (reviews, attempts) will be <span className="font-semibold">kept</span> in the database, so they can be re-enrolled later. This action can be reversed by re-enrolling the student.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
              data-testid="confirm-delete-enrollment"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Extend subscription dialog */}
      <Dialog open={!!extendTarget} onOpenChange={(o) => !o && setExtendTarget(null)}>
        <DialogContent className="max-w-md" data-testid="extend-months-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-indigo-600" />
              Extend subscription
            </DialogTitle>
            <DialogDescription>
              Add extra months of access to <span className="font-semibold">{extendTarget?.course_name}</span>. If the
              subscription has already expired, access will be restored and the timer starts from today.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm font-semibold" htmlFor="extend-months-input">Months to add</label>
            <input
              id="extend-months-input"
              type="number"
              min="1"
              max="60"
              value={extendMonths}
              onChange={(e) => setExtendMonths(Math.max(1, Math.min(60, parseInt(e.target.value || "1", 10))))}
              className="w-full h-10 px-3 border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="extend-months-input"
            />
            <p className="text-[11px] text-slate-500">Between 1 and 60 months.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setExtendTarget(null)} disabled={busyCid === extendTarget?.course_id}>Cancel</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={confirmExtend}
              disabled={busyCid === extendTarget?.course_id}
              data-testid="confirm-extend"
            >
              {busyCid === extendTarget?.course_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarPlus className="w-4 h-4 mr-2" />}
              Extend
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
